import React, {
    type ChangeEventHandler, type FocusEventHandler, useCallback,

    useContext,
    useEffect, useId, useMemo, useState,
} from 'react';

import {MessageContext} from 'src/contexts/message-context';

import {FieldDescription, Types as FieldDescriptionTypes} from '@pb/kit-component-field-description';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Loading, {Types} from '@pb/kit-component-loading';


import {inputAddorment, inputAddormentItem} from '../input.module.styl';

import {NumberInputTypes} from './constants';
import {validateInternalState} from './utils/validateInternalState';
import {validateOnBlurChange} from './utils/validateOnBlurChange';


import type {NumberInputType} from './typedef';

type Props = {
    id?: string,
    labelId?: string,
    value?: number,
    inputType?: NumberInputType,
    onBlur: (value?: number) => void,
    onChange?: (value?: number) => void,

    displayedValue: string,
    description?: string,

    placeholder?: string,
    maxValue?: number,
    minValue?: number,
    readOnly?: boolean,
    disabled?: boolean,
    valid?: boolean,
    busy?: boolean,
    width?: number | string,
    label?: React.ReactNode,

    endIcon?: React.ReactElement | null,
};

export const NumberInput = (props: Props) => {
    const {
        value,
        inputType = NumberInputTypes.POSITIVE_INTEGER,
        onBlur,
        onChange,

        displayedValue,
        description,
        width,

        readOnly,
        disabled,
        placeholder,
        minValue,
        maxValue,
        valid,
        busy,
        label,

        endIcon,
    } = props;

    const [internalValue, setInternalValue] = useState<string | undefined>(value?.toString());
    const [isFocused, setFocused] = useState(false);
    const {setErrorMessage} = useContext(MessageContext);

    const id = useId();
    const labelId = useId();
    const descriptionId = useId();

    useEffect(() => {
        setInternalValue(value?.toString());
    }, [value]);

    const color = useMemo(() => {
        if (!busy && !isFocused && !readOnly) {
            if (valid !== undefined && !valid) {
                return 'ERROR';
            } else if (valid) {
                return 'SUCCESS';
            }
        }

        return undefined;
    }, [valid, busy, isFocused, readOnly]);

    const blurInput = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
        setFocused(false);
        if (!readOnly) {
            validateOnBlurChange({
                value: internalValue,
                inputType,
                onBlur,
                onError: () => setErrorMessage('Invalid value'),
            });
        }
    }, [onBlur, internalValue, inputType, readOnly, setErrorMessage]);

    const focusInput = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
        setInternalValue(value?.toString());
        setFocused(true);
    }, [value]);

    const changeInput = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const enteredValue = event.currentTarget.value.trim();

        if (!enteredValue) {
            setInternalValue(undefined);
            if (onChange) {
                onChange(undefined);
            }
            return;
        }

        if (validateInternalState({enteredValue, inputType, minValue, maxValue})) {
            setInternalValue(enteredValue);
            if (onChange) {
                validateOnBlurChange({
                    value: enteredValue,
                    inputType,
                    onBlur: onChange,
                    onError: () => {},
                });
            }
        }
    }, [maxValue, inputType, minValue, onChange]);

    const endAdornment = useMemo(() => {
        return (
            <div className={inputAddorment}>
                <Group gap="xs">
                    {!isFocused && !busy && !readOnly && (
                        <>
                            {(valid !== undefined && !valid)
                                ? <Icon type={Icons.ERROR} size="Size_14" className="error-color"/>
                                : null}
                            {valid
                                ? <Icon type={Icons.CHECK} size="Size_14" className="success-color"/>
                                : null}
                        </>
                    )}
                    {busy ? <Loading type={Types.ICON} fast={true}/> : null}
                    {endIcon}
                    {readOnly ? <Icon className={inputAddormentItem} type={Icons.LOCK} size="Size_14"/> : null}
                </Group>
            </div>
        );
    }, [readOnly, isFocused, endIcon, busy, valid]);

    return (
        <div style={{width}} id={props.id || id}>
            {label ? (
                <label htmlFor={id} id={props.labelId || labelId}>
                    <b>{label}</b>
                </label>
            ) : null}
            {isFocused ? (
                <Input
                    id={`${props.id || id}-edit-input`}

                    autoComplete="off"
                    autoCorrect="off"
                    autoFocus={true}
                    color={color}

                    value={internalValue !== undefined && internalValue !== null
                        ? internalValue.toString()
                        : ''}
                    endAdornment={endAdornment}

                    aria-labelledby={label ? (props.labelId || labelId) : undefined}
                    aria-describedby={description ? descriptionId : undefined}
                    aria-invalid={valid !== undefined && !valid}

                    onFocus={() => setFocused(true)}
                    onChange={changeInput}
                    onBlur={blurInput}
                    onKeyUp={event => {
                        if (event.key === 'Enter') {
                            event.currentTarget.blur();
                        }
                    }}

                    readOnly={readOnly}
                    disabled={disabled}
                />
            ) : (
                <Input
                    id={`${props.id || id}-displayed-input`}
                    value={displayedValue}
                    onFocus={focusInput}
                    onChange={() => {}}
                    color={color}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    disabled={disabled}
                    endAdornment={endAdornment}

                    aria-labelledby={label ? (props.labelId || labelId) : undefined}
                    aria-describedby={description ? descriptionId : undefined}
                    aria-invalid={valid !== undefined && !valid}
                />
            )}
            {!!description && (
                <FieldDescription
                    aria-live="polite"
                    id={descriptionId}
                    color={valid !== undefined && !valid
                        ? FieldDescriptionTypes.ERROR
                        : FieldDescriptionTypes.DEFAULT}>
                    {description}
                </FieldDescription>
            )}
        </div>
    );
};
